import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    columns: number;
    rows: number;
    columnGap: number;
    rowGap: number;
    columnTemplate: string;
    rowTemplate: string;
    copied: boolean;
}

export default class CssGridGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        columns: 3,
        rows: 3,
        columnGap: 12,
        rowGap: 12,
        columnTemplate: '',
        rowTemplate: '',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get colTemplate(): string {
        return this.state.columnTemplate.trim() || `repeat(${this.state.columns}, 1fr)`;
    }

    private get rowTemplateValue(): string {
        return this.state.rowTemplate.trim() || `repeat(${this.state.rows}, 80px)`;
    }

    private get cssCode(): string {
        return `display: grid;\ngrid-template-columns: ${this.colTemplate};\ngrid-template-rows: ${this.rowTemplateValue};\ncolumn-gap: ${this.state.columnGap}px;\nrow-gap: ${this.state.rowGap}px;`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { columns, rows, columnGap, rowGap, columnTemplate, rowTemplate, copied } = this.state;
        const previewStyle: React.CSSProperties = {
            display: 'grid',
            gridTemplateColumns: this.colTemplate,
            gridTemplateRows: this.rowTemplateValue,
            columnGap: `${columnGap}px`,
            rowGap: `${rowGap}px`,
        };
        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Grid size</span></div>
                    <div className={genStyles.field}>
                        <label>Columns: <strong>{columns}</strong></label>
                        <input type="range" min={1} max={12} value={columns}
                            onChange={e => this.setState({ columns: +e.target.value, columnTemplate: '' })} />
                    </div>
                    <div className={genStyles.field}>
                        <label>Rows: <strong>{rows}</strong></label>
                        <input type="range" min={1} max={8} value={rows}
                            onChange={e => this.setState({ rows: +e.target.value, rowTemplate: '' })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Gaps</span></div>
                    <div className={genStyles.field}>
                        <label>column-gap: <strong>{columnGap}px</strong></label>
                        <input type="range" min={0} max={50} value={columnGap}
                            onChange={e => this.setState({ columnGap: +e.target.value })} />
                    </div>
                    <div className={genStyles.field}>
                        <label>row-gap: <strong>{rowGap}px</strong></label>
                        <input type="range" min={0} max={50} value={rowGap}
                            onChange={e => this.setState({ rowGap: +e.target.value })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Custom templates (override count)</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="grid-col-tpl">grid-template-columns</label>
                        <input id="grid-col-tpl" type="text" value={columnTemplate} placeholder="repeat(3, 1fr)"
                            onChange={e => this.setState({ columnTemplate: e.target.value })} spellCheck={false} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="grid-row-tpl">grid-template-rows</label>
                        <input id="grid-row-tpl" type="text" value={rowTemplate} placeholder="repeat(3, 80px)"
                            onChange={e => this.setState({ rowTemplate: e.target.value })} spellCheck={false} />
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={`${columns}×${rows}`}
                preview={
                    <div style={{ ...previewStyle, width: '100%' }}>
                        {Array.from({ length: columns * rows }, (_, i) => (
                            <div key={i} style={{ background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, minHeight: 40, fontWeight: 600 }}>{i + 1}</div>
                        ))}
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
